import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last and numeric-vs-string live in the
// library, not in a per-module copy.
import {sortRecords} from '@flexinet/ui-components';

import {fetchRecords} from '../services/BillingProvidersService';
import {BillingProvider} from '../types';
import {useBillingProvidersFilter} from './useBillingProvidersFilter';
import {billingProvidersQueryKeys} from './billingProvidersQueryKeys';

// Kept here for the callers that imported it from this file.
export {useBillingProvidersFilter} from './useBillingProvidersFilter';

// Page size is a server concern fixed by the module (§9), never a user choice.
const PAGE_SIZE = 10;

// NEWEST FIRST, on `created`: the list index's sort key (record-type-created-index,
// ScanIndexForward false), so first paint agrees with the server's own order.
// `created` is nullable in the schema (AWSDateTime), but every listed row carries it:
// the create resolver always sets it, and a row without it would not be in that index.
//
// The billing-api resolver drops the `sort` argument (billing-providers/list.ts passes
// only filter, limit and nextToken), so ordering is applied here rather than requested.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<BillingProvider> = {
    columnAccessor: 'created',
    direction: 'desc',
};

const matchesKeyword = (item: BillingProvider, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [item.name, item.key, item.description, item.status];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

// The resolver ignores filter.status (FNP-1895), so the status filter is ALSO applied to
// the loaded rows. Without this the default "Enabled" filter showed Disabled providers.
const matchesFilters = (item: BillingProvider, filters: Record<string, string>): boolean =>
    !filters.status || item.status === filters.status;

export const useBillingProviders = () => {
    const queryClient = useQueryClient();
    const {filters} = useBillingProvidersFilter();

    // Component state, not a module-scoped store. The search input (TableSearchForm) and
    // the table that reads the keyword are the SAME component, RecordsTable, so there is
    // no sibling for a store to reach. BillingProvidersAutocomplete reads `items`, the
    // unfiltered loaded set, and must not inherit a table search.
    const [keyword, setKeyword] = useState('');

    // Sort is how one operator is viewing the table right now: component state.
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<BillingProvider>>(DEFAULT_SORT_STATUS);

    // Only what the server is sent is in the key. The keyword is applied client-side
    // below, so keying on it would refetch identical pages per keystroke.
    const query = useInfiniteQuery({
        queryKey: billingProvidersQueryKeys.list(filters),
        queryFn: ({pageParam}: { pageParam: string | null }) => fetchRecords({
            limit: PAGE_SIZE,
            nextToken: pageParam,
            filter: filters,
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        // Providers change only through this module, and every mutation invalidates
        // ['billing-providers'], so 30s stops a remount from refetching without hiding an
        // edit.
        staleTime: 30_000,
    });

    const items: BillingProvider[] =
        query.data?.pages?.flatMap(page => (page && 'items' in page && page.items) ? page.items : []) ?? [];

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Client-side filter and sort cover the pages FETCHED SO FAR. Under token
    // pagination the browser cannot order or search rows it has not loaded; that is a
    // property of the model, not a bug (§9).
    const filteredItems = items.filter(item => matchesFilters(item, filters));
    const visibleItems = sortRecords(
        filteredItems.filter(item => matchesKeyword(item, needle)),
        sortStatus
    );

    const lastPage = query.data?.pages?.[query.data.pages.length - 1];

    return {
        // The loaded set BEFORE any client-side filtering: what the provider picker lists.
        items,
        // After the status filter, before the keyword: what the footer's "N of M loaded"
        // is measured against.
        filteredItems,
        visibleItems,
        keyword,
        filters,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingMore: query.isFetchingNextPage,
        // The server's own cursor, never a stand-in: opaque to this client (§9).
        nextToken: query.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        // Loads the NEXT page. Guarded on isFetchingNextPage too, so scroll-to-bottom and
        // Load More cannot start overlapping requests for the same cursor.
        fetchMoreRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        // Reload starts again from page 1. Invalidation (not refetch) so every
        // useBillingProviders instance, the table's and the Reload button's, sees it.
        reloadRecords: () => queryClient.invalidateQueries({queryKey: billingProvidersQueryKeys.all}),
        handleFilterByKeyword: (newKeyword: string) => setKeyword(newKeyword),
    };
};
